import { listWorkspaces } from '@modusign/openapi-client-user';
import { ListUserWorkspacesResponseWorkspace } from '@modusign/openapi-client-user/generated/model';
import { LoginByTokenResponseDto } from '@modusign/openapi-client-user/model';
import { AxiosError } from 'axios';
import i18n from 'i18next';
import { push } from 'redux-first-history';
import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { v4 } from 'uuid';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import authService, { Tokens } from '@app/libs/auth/authService';
import documentAuthService from '@app/libs/auth/documentAuthService';
import { isAxiosError } from '@app/libs/axios/isAxiosError';
import { isKakaoInAppBrowser } from '@app/libs/browserUserAgent';
import { setDatadogContextForContractProgress } from '@app/libs/datadog';
import { logger } from '@app/libs/logger';
import { login } from '@app/libs/login';
import { retryIf } from '@app/libs/redux/saga/effect';
import getSensitiveDocumentTitleKeywords from '@app/libs/validation/getSensitiveDocumentTitleKeywords';
import workspaceService from '@app/libs/workspaceService';
import { InPersonHostPageLocationState } from '@app/routes/InPersonHost';
import { SigningAuthenticationPageState } from '@app/routes/Signing/Authentication';
import { isAuthenticated } from '@app/store/account/selectors';
import { signOut } from '@app/store/account/sign-out/actions';
import { MODAL_CLOSE, PARTICIPANT_STATUS_FAILURE } from '@app/store/actionTypes';
import Document from '@app/store/document/Document';
import Status from '@app/store/document/Status';
import DocumentRole from '@app/store/documents/DocumentRole';
import Participant from '@app/store/documents/Participant';
import Signing from '@app/store/documents/Signing';
import SigningMethodType from '@app/store/documents/SigningMethodType';
import Host from '@app/store/Host';
import { modalClose, modalOpen } from '@app/store/modal/actions';
import {
  fetchParticipantStatus,
  fetchParticipantStatusAfterLoginWithToken,
  fetchParticipantStatusFailure,
  fetchParticipantStatusSuccess,
} from '@app/store/participant/actions';
import {
  getContactInfo,
  getDocumentId,
  getHost,
  getParticipantId,
  getRequester,
  getSigningMethodType,
} from '@app/store/participant/selectors';
import {
  DocumentDetail,
  getDocumentsByIdDeprecated,
} from '@app/store/transport-layer/document/documents';
import { fetchLoginLink } from '@app/store/transport-layer/document/documents/participants/access';
import { fetchMigrationLoginLink } from '@app/store/transport-layer/document/documents/participants/migration-access';
import fetchAccessTokenByParticipantLoginLink from '@app/store/transport-layer/fetchAccessTokenByParticipantLoginLink';
import { getEmail, getProviders } from '@app/store/user/user/selectors';

import { SigningMethodTypeState } from './reducers/signingMethodType';

function* setAccessTokenToAuthService(tokens: Tokens) {
  const isAuth: ReturnType<typeof isAuthenticated> = yield select(isAuthenticated);
  const userProviders: ReturnType<typeof getProviders> = yield select(getProviders);
  // 카카오 싱크 자동로그인시 로그아웃 시키지 않고 서명 이후 기존 토큰을 복구합니다.
  if (isKakaoInAppBrowser() && userProviders.some((p) => p.type === 'kakao')) {
    documentAuthService.changeTokens(tokens);
  } else if (isAuth) {
    /**
     * 링크 유입시 기존 유저의 이메일과 연락처 정보가 다를 경우
     * 기존에 로그인되어있는 유저를 로그아웃시킵니다.
     * @see https://github.com/lawoafactory/modusign-client/issues/941
     */
    yield put(signOut());
  }
  authService.setTokens(tokens);
}

interface GetLoginLinkParam {
  contactInfo?: string;
  documentId: string;
  linkKey?: string | null;
  participantId?: string;
  token?: string | null;
  expiry?: number;
  signingMethodType?: 'email' | 'kakao' | 'SECURE_LINK' | 'IN_PERSON';
}

function* getLoginLink({
  contactInfo,
  documentId,
  linkKey,
  participantId,
  signingMethodType,
  token,
  expiry,
}: GetLoginLinkParam) {
  let loginLink;
  /**
   * 기존 링크, 새 링크를 구분하여 로그인합니다.
   *
   * 기존 링크는 documentId, linkKey, participantId, signingMethodType
   * 네 가지 요소로 로그인할 수 있는 loginLink를 조회합니다.
   *
   * 새로 개선된 링크는 documentId, participantId, token
   * 세 가지 요소로 로그인할 수 있는 loginLink를 조회합니다.
   *
   * @see https://github.com/lawoafactory/modusign-client/issues/4316
   */
  if (contactInfo && linkKey && participantId && signingMethodType) {
    const result: { loginLink: string } = yield call(fetchMigrationLoginLink, {
      contactInfo,
      documentId,
      linkKey,
      participantId,
      signingMethodType,
    });
    loginLink = result.loginLink;
  } else if (contactInfo && participantId && token && signingMethodType) {
    const result: { loginLink: string } = yield call(fetchLoginLink, {
      contactInfo,
      documentId,
      participantId,
      signingMethodType,
      token,
      expiry,
    });
    loginLink = result.loginLink;
  }

  return loginLink;
}

function* getDocument(documentId: string) {
  // 임베디드 보안링크와 같이 문서를 만들고 바로 서명 페이지로 접근하는 경우
  // 서버 비동기 처리 문제로 참여자가 document 요청시 403 에러가 잠시동안 발생할 수 있습니다.
  // 이 경우 5번 다시 시도 후 지속적으로 발생시 재시도 모달을 띄웁니다.
  // 참고: https://modusign.slack.com/archives/CSJQ81X0F/p1652873335705269
  // 참고: https://modusign.slack.com/archives/C01PP3J0U3H/p1640597687215200
  let result: Document | null = null;
  while (result === null) {
    try {
      result = yield call(
        retryIf,
        5,
        1000,
        (e: AxiosError) => e.response?.status === 403,
        getDocumentsByIdDeprecated,
        {
          documentId,
          workspaceId: workspaceService.getWorkspaceId(),
        }
      );
    } catch (e) {
      if (e.response?.status === 403) {
        const modalId = v4();
        yield put(
          modalOpen('CONFIRM', {
            content: i18n.t('participant-forbiddenMessage'),
            closeText: i18n.t('participant-retryButton'),
            modalId,
          })
        );
        while (true) {
          const action: ReturnType<typeof modalClose> = yield take([MODAL_CLOSE]);
          if (action.payload.modalId === modalId) {
            // MODAL_CLOSE 가 take 되었다면 컨펌이 승인되었으므로 while문을 벗어납니다.
            break;
          }
        }
      } else {
        throw e;
      }
    }
  }

  return result;
}

interface ParticipantState {
  contactInfo: string;
  documentId: string;
  isRequesterLink: boolean;
  participantId: string;
  requester: {
    email: string;
    name: string;
  };
  signingMethodType: string;
}

interface RedirectToDocumentParams {
  currentParticipant: Participant;
  exitPath?: string;
  state: ParticipantState;
  status: Status;
  signings: Signing[];
  currentSigningOrder: number;
}

function* redirectToDocument({
  currentParticipant,
  exitPath,
  state,
  status,
  signings,
  currentSigningOrder,
}: RedirectToDocumentParams) {
  const { contactInfo, documentId, participantId, requester, signingMethodType } = state;

  if (status === Status.ON_PROCESSING) {
    yield put(modalOpen('PARTICIPANT_PROCESSING_MODAL', { documentId, exitPath, participantId }));
  } else if (status === Status.PROCESSING_FAILED) {
    yield put(modalOpen('PARTICIPANT_PROCESSING_FAILED_MODAL'));
  } else if (status === Status.PENDING) {
    yield put(
      push('/document/aborted', {
        contactInfo,
        documentId,
        exitPath,
        requester,
      })
    );
  } else if (status === Status.COMPLETED) {
    yield put(
      push('/document/completed', {
        contactInfo,
        documentId,
        participantId,
        exitPath,
      })
    );
  } else if (
    currentSigningOrder !== currentParticipant.signingOrder ||
    signings
      .filter((signing) => !signing.expiredAt)
      .map((signing) => signing.participantId)
      .includes(currentParticipant.participantId)
  ) {
    yield put(
      push('/document/wait-for-signing', {
        documentId,
        participantId,
        exitPath,
      })
    );
  } else if (!currentParticipant.signingExpiryDate.isValid) {
    yield put(
      push('/signing/expired', {
        documentId,
        exitPath,
        participantId,
      })
    );
  } else {
    yield put(
      push<SigningAuthenticationPageState>('/signing/authentication', {
        contactInfo,
        documentId,
        exitPath,
        participantId,
        signingMethodType: signingMethodType as SigningMethodType,
      })
    );
  }
}

function* redirectToDocumentWhenUserRoleIsHost({
  exitPath,
  documentId,
  host,
  requester,
  status,
}: {
  exitPath: string | undefined;
  documentId: string;
  host: Host;
  requester: {
    email: string;
    name: string;
  };
  status: Status;
}) {
  switch (status) {
    case Status.ON_GOING:
      yield put(
        push<InPersonHostPageLocationState>('/in-person/host', {
          exitPath,
          documentId,
          currentHostDataLabel: host.dataLabel,
        })
      );
      break;
    case Status.ON_PROCESSING:
      yield put(
        modalOpen('PARTICIPANT_PROCESSING_MODAL', {
          documentId,
          exitPath,
          participantId: host.participantId,
        })
      );
      break;
    case Status.PROCESSING_FAILED:
      yield put(modalOpen('PARTICIPANT_PROCESSING_FAILED_MODAL'));
      break;
    case Status.PENDING:
      yield put(
        push('/document/aborted', {
          documentId,
          exitPath,
          requester,
          contactInfo: host.contactInfo,
        })
      );
      break;
    case Status.COMPLETED:
      yield put(
        push('/document/completed', {
          documentId,
          exitPath,
          contactInfo: host.contactInfo,
          participantId: host.participantId,
        })
      );
      break;
    default:
      break;
  }
}

export function* processParticipantStatusSuccess({
  payload: { exitPath, participants, status, redirect, signings, userRole, currentSigningOrder },
}: ReturnType<typeof fetchParticipantStatusSuccess>) {
  const contactInfo: ReturnType<typeof getContactInfo> = yield select(getContactInfo);
  const documentId: ReturnType<typeof getDocumentId> = yield select(getDocumentId);
  const participantId: ReturnType<typeof getParticipantId> = yield select(getParticipantId);
  const requester: ReturnType<typeof getRequester> = yield select(getRequester);
  const signingMethodType: ReturnType<typeof getSigningMethodType> =
    yield select(getSigningMethodType);
  const host: ReturnType<typeof getHost> = yield select(getHost);

  if (redirect) {
    if (userRole === DocumentRole.HOST && host) {
      yield call(redirectToDocumentWhenUserRoleIsHost, {
        exitPath,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        documentId: documentId!,
        host,
        requester,
        status,
      });
    } else {
      yield call(redirectToDocument, {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        currentParticipant: participants.find((p) => p.participantId === participantId)!,
        exitPath,
        state: {
          contactInfo,
          documentId,
          isRequesterLink: false,
          participantId,
          requester,
          signingMethodType,
        },
        status,
        signings,
        currentSigningOrder,
      } as RedirectToDocumentParams);
    }
  }
}

export function* notifyParticipantStatusFailure({
  payload: error,
}: ReturnType<typeof fetchParticipantStatusFailure>) {
  if (error.message === 'Unauthenticated participant') {
    yield put(
      modalOpen('CONFIRM', {
        content: i18n.t('participant-unauthenticatedParticipant'),
      })
    );
  } else {
    yield put(
      modalOpen('CONFIRM', {
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}
/**
 * @describe signer, host 선택
 */
function getParticipant({
  document: { participants, requester, hosts, currentSigningOrder },
  currentParticipantId,
  isSigningRightNow,
}: {
  document: Document;
  currentParticipantId?: string;
  isSigningRightNow: boolean;
}) {
  const isInPersonSigner = (signer: Participant, host: Host | null) =>
    signer.signingMethod.methodType === SigningMethodType.IN_PERSON &&
    signer.host?.dataLabel === host?.dataLabel;

  let host: Host | null;
  let signer: Participant;

  const sendingSigner = participants.find(
    (p) =>
      p.signingMethod.contactInfo === requester.email &&
      p.signingMethod.methodType !== SigningMethodType.IN_PERSON &&
      currentSigningOrder === p.signingOrder
  );

  if (isSigningRightNow && sendingSigner) {
    // 전송방식 서명자가 있다면 전송방식 서명자가 우선합니다.
    // 전송방식 서명자는 host가 없습니다.
    host = null;
    signer = sendingSigner;
  } else if (isSigningRightNow) {
    host = hosts.find((h) => h.contactInfo === requester.email) ?? null;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    signer = participants.find(
      (p) => p.signingOrder === currentSigningOrder && isInPersonSigner(p, host)
    )!;
  } else {
    host = hosts.find((h) => h.participantId === currentParticipantId) ?? null;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    signer = participants.find(
      (p) => p.participantId === currentParticipantId || isInPersonSigner(p, host)
    )!;
  }

  return {
    host,
    requester,
    signer: {
      participantId: signer.participantId,
      contactInfo: signer.signingMethod.contactInfo,
      signingMethodType: signer.signingMethod.methodType,
    },
  };
}

type ParticipantStatusReturn = Pick<
  Document,
  'status' | 'signings' | 'requester' | 'participants' | 'currentSigningOrder'
> & {
  host: Host | null;
  signer: {
    contactInfo: string;
    participantId: string;
    signingMethodType: SigningMethodTypeState;
  };
  userRole: DocumentRole;
};

function* fetchParticipantStatusWithCurrentParticipantId({
  documentId,
  participantId: currentParticipantId,
  isSigningRightNow = false,
}: {
  documentId: string;
  participantId?: string;
  isSigningRightNow?: boolean;
}) {
  const document: Document = yield call(getDocument, documentId);
  const { status, signings, participants, currentSigningOrder } = document;
  const { host, signer, requester } = getParticipant({
    document,
    currentParticipantId,
    isSigningRightNow,
  });

  const participantStatus: ParticipantStatusReturn = {
    status,
    signings,
    requester,
    participants,
    host,
    signer,
    currentSigningOrder,
    userRole: host ? DocumentRole.HOST : DocumentRole.PARTICIPANT,
  };

  return participantStatus;
}

export function* setupParticipantSaga({ payload }: ReturnType<typeof fetchParticipantStatus>) {
  const { redirect, exitPath, isSigningRightNow } = payload;

  const {
    status,
    signings,
    requester,
    participants,
    host,
    signer,
    userRole,
    currentSigningOrder,
  }: ParticipantStatusReturn = yield call(fetchParticipantStatusWithCurrentParticipantId, {
    participantId: payload.participantId,
    documentId: payload.documentId,
    isSigningRightNow,
  });

  yield put(
    fetchParticipantStatusSuccess({
      exitPath,
      isSigningRightNow,
      redirect,
      participants,
      requester,
      signings,
      status,
      userRole,
      host,
      signer,
      currentSigningOrder,
    })
  );
}

export function* setupParticipantStatusAfterLogin({
  payload,
}: ReturnType<typeof fetchParticipantStatusAfterLoginWithToken>) {
  try {
    const { contactInfo, token, linkKey } = payload;

    const hasLinkKeyOrToken = token || linkKey;
    const userEmail: ReturnType<typeof getEmail> = yield select(getEmail);
    // @ts-expect-error FIXME
    const loginLink = yield call(getLoginLink, payload);

    if (!loginLink) {
      throw new Error(
        '잘못된 접근입니다. 문서에 접근할 수 없습니다. 로그인 하거나 이메일 또는 카카오톡 링크로 다시 접속해 주세요.'
      );
    }

    if (hasLinkKeyOrToken && contactInfo !== userEmail) {
      const tokens: LoginByTokenResponseDto = yield call(fetchAccessTokenByParticipantLoginLink, {
        loginLink,
      });

      yield call(setAccessTokenToAuthService, tokens);
      yield call(login, { type: 'token', tokens });
    }

    const workspace: ListUserWorkspacesResponseWorkspace = yield call(
      findWorkspaceAccessibleToDocument,
      payload.documentId
    );
    workspaceService.setWorkspaceId(workspace.id);

    const {
      status,
      signings,
      requester,
      participants,
      host,
      signer,
      userRole,
      currentSigningOrder,
    }: ParticipantStatusReturn = yield call(fetchParticipantStatusWithCurrentParticipantId, {
      participantId: payload.participantId,
      documentId: payload.documentId,
      isSigningRightNow: false,
    });

    setDatadogContextForContractProgress({
      contact: payload.contactInfo,
      documentId: payload.documentId,
      participantId: payload.participantId,
    });
    yield put(
      fetchParticipantStatusSuccess({
        exitPath:
          signer.signingMethodType === 'IN_PERSON'
            ? window.location.pathname + window.location.search
            : undefined,
        isSigningRightNow: false,
        redirect: true,
        participants,
        requester,
        signings,
        status,
        userRole,
        host,
        signer,
        currentSigningOrder,
      })
    );
  } catch (e) {
    yield put(fetchParticipantStatusFailure(e));
  }
}

/**
 * @description 유저의 워크스페이스가 여러개 있을 때 어떤 워크스페이스로 문서에 접근할 수 있는지 알 수 없습니다. (서명요청 링크로 접근시, documentId는 링크에 포함되지만, workspaceId는 포함되지 않습니다.)
 * 그리고 접근 권한에 따라서, 같은 문서id라도 2가지 workspaceId로 접근할 수 있습니다.
 * 즉. 이 서명요청 건을 인식할 때, 어떤 workspaceId에 있는 어떤 문서를 접근해야하는 지 클라이언트는 시도하여 알아내야합니다.
 *
 * 기본적으로는 활성워크스페이스(공용 워크스페이스. 없으면 개인 워크스페이스)로 서명요청이지만..
 * 요구사항 중 민감문서의 경우, 개인워크스페이스로 보내지기때문에, documentId로 접근할 때, 개인워크스페이스로 접근을 시도해야합니다. 그래서 try가 많아졌습니다.
 * 도메인 룰에 따라 작업한 후 문서에 접근 가능한 워크스페이스를 반환합니다.
 */
async function findWorkspaceAccessibleToDocument(
  documentId: string
): Promise<ListUserWorkspacesResponseWorkspace> {
  const SENSITIVE_ALERT_MESSAGE = '공용 워크스페이스에서는 민감한 문서에 접근할 수 없습니다.';
  const { workspaces } = await listWorkspaces();

  /**
   * PUBLIC을 우선하여 sort하여, 첫번째 워크스페이스를 active로 인식합니다.
   * 즉 active 다음에 오는 워크스페이스는, PRIVATE 워크스페이스거나 undefined입니다.(maybeWorkspace)
   */
  const [activeWorkspace, maybeWorkspace] = workspaces.sort((ws) =>
    ws.type === 'PUBLIC' ? -1 : 1
  ) as [ListUserWorkspacesResponseWorkspace, ListUserWorkspacesResponseWorkspace | undefined];

  let document: DocumentDetail | undefined;

  try {
    document = await getDocumentsByIdDeprecated({ documentId, workspaceId: activeWorkspace.id });

    const isSensitiveDocument =
      document && getSensitiveDocumentTitleKeywords(document.name).length > 0;

    if (activeWorkspace.type === 'PUBLIC' && isSensitiveDocument) {
      throw new Error(SENSITIVE_ALERT_MESSAGE);
    }

    return activeWorkspace;
  } catch (firstTrialError) {
    logger.info(
      `Failed while attempting to access the document to sign with workspace ${activeWorkspace.id}. ${firstTrialError.message}`,
      firstTrialError
    );

    if (
      (isAxiosError(firstTrialError) && firstTrialError.response?.status === 403) ||
      firstTrialError.message === SENSITIVE_ALERT_MESSAGE
    ) {
      if (maybeWorkspace) {
        try {
          document = await getDocumentsByIdDeprecated({
            documentId,
            workspaceId: maybeWorkspace.id,
          });

          return maybeWorkspace;
        } catch (secondTrialError) {
          logger.error(`Can't access the document by any workspace`, secondTrialError);
          throw secondTrialError;
        }
      } else {
        logger.error(`Can't access the document by any workspace`, firstTrialError);
        throw firstTrialError;
      }
    }

    throw firstTrialError;
  }
}

export default function* saga() {
  yield all([
    takeLatest(fetchParticipantStatusAfterLoginWithToken.type, setupParticipantStatusAfterLogin),
    takeLatest(fetchParticipantStatus.type, setupParticipantSaga),
    takeEvery(fetchParticipantStatusSuccess.type, processParticipantStatusSuccess),
    takeEvery(PARTICIPANT_STATUS_FAILURE, notifyParticipantStatusFailure),
  ]);
}
